import React, { useState, useEffect } from 'react';
import { X, Users, Plus, Trash2, Shield, Crown, User, UserPlus, Settings as SettingsIcon, Mail, Clock, CreditCard, Zap, RotateCcw } from 'lucide-react';
import { fetchTeams, createTeam, updateTeam, deleteTeam, addTeamMember, updateMemberRole, removeMember, updateTeamPermissions } from '../services/teamsApi';
import { fetchUsers, inviteUser, fetchInvitations, updateUserStatus, updateUserCredits, updateUserAdminStatus, resetUserCredits } from '../services/usersApi';
import ConfirmationModal from './ConfirmationModal';

const Teams = ({ isOpen, onClose, models }) => {
  const [activeTab, setActiveTab] = useState('teams');
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [showEditCredits, setShowEditCredits] = useState(false);
  const [showResetCredits, setShowResetCredits] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [newMember, setNewMember] = useState({ email: '', role: 'MEMBER' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [newCredits, setNewCredits] = useState(50000);
  const [permissions, setPermissions] = useState({
    allowedModels: [],
    allowedBurstGroups: {},
    enableBurstMode: true,
    monthlyCredits: null,
  });

  useEffect(() => {
    if (isOpen) {
      loadTeams();
      if (activeTab === 'users') {
        loadUsers();
        loadInvitations();
      }
    }
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (selectedTeam?.permissions) {
      setPermissions({
        allowedModels: selectedTeam.permissions.allowedModels || [],
        allowedBurstGroups: selectedTeam.permissions.allowedBurstGroups || {},
        enableBurstMode: selectedTeam.permissions.enableBurstMode ?? true,
        monthlyCredits: selectedTeam.permissions.monthlyCredits || null,
      });
    }
  }, [selectedTeam]);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const data = await fetchTeams();
      setTeams(data);
      if (data.length > 0 && !selectedTeam) {
        setSelectedTeam(data[0]);
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadInvitations = async () => {
    try {
      const data = await fetchInvitations();
      setInvitations(data);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    }
  };

  const handleCreateTeam = async () => {
    try {
      const team = await createTeam(newTeam);
      setTeams([...teams, team]);
      setSelectedTeam(team);
      setShowCreateForm(false);
      setNewTeam({ name: '', description: '' });
    } catch (error) {
      console.error('Failed to create team:', error);
      alert('Failed to create team');
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;
    
    try {
      await deleteTeam(teamToDelete.id);
      setTeams(teams.filter(t => t.id !== teamToDelete.id));
      if (selectedTeam?.id === teamToDelete.id) {
        setSelectedTeam(teams[0] || null);
      }
      setDeleteModalOpen(false);
      setTeamToDelete(null);
    } catch (error) {
      console.error('Failed to delete team:', error);
      alert('Failed to delete team');
    }
  };

  const handleAddMember = async () => {
    if (!selectedTeam) return;
    
    try {
      const member = await addTeamMember(selectedTeam.id, newMember);
      const updatedTeam = {
        ...selectedTeam,
        members: [...selectedTeam.members, member],
      };
      setSelectedTeam(updatedTeam);
      setTeams(teams.map(t => t.id === selectedTeam.id ? updatedTeam : t));
      setShowAddMember(false);
      setNewMember({ email: '', role: 'MEMBER' });
    } catch (error) {
      console.error('Failed to add member:', error);
      alert(error.message);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    if (!selectedTeam) return;
    
    try {
      await updateMemberRole(selectedTeam.id, memberId, newRole);
      const updatedTeam = {
        ...selectedTeam,
        members: selectedTeam.members.map(m => 
          m.id === memberId ? { ...m, role: newRole } : m
        ),
      };
      setSelectedTeam(updatedTeam);
      setTeams(teams.map(t => t.id === selectedTeam.id ? updatedTeam : t));
    } catch (error) {
      console.error('Failed to update role:', error);
      alert('Failed to update member role');
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedTeam || !memberToRemove) return;
    
    try {
      await removeMember(selectedTeam.id, memberToRemove.id);
      const updatedTeam = {
        ...selectedTeam,
        members: selectedTeam.members.filter(m => m.id !== memberToRemove.id),
      };
      setSelectedTeam(updatedTeam);
      setTeams(teams.map(t => t.id === selectedTeam.id ? updatedTeam : t));
      setMemberToRemove(null);
    } catch (error) {
      console.error('Failed to remove member:', error);
      alert('Failed to remove member');
    }
  };

  const handleUpdatePermissions = async () => {
    if (!selectedTeam) return;
    
    try {
      const updated = await updateTeamPermissions(selectedTeam.id, permissions);
      const updatedTeam = {
        ...selectedTeam,
        permissions: updated,
      };
      setSelectedTeam(updatedTeam);
      setTeams(teams.map(t => t.id === selectedTeam.id ? updatedTeam : t));
      setShowPermissions(false);
    } catch (error) {
      console.error('Failed to update permissions:', error);
      alert('Failed to update permissions');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'OWNER':
        return <Crown className="w-4 h-4 text-yellow-600" />;
      case 'ADMIN':
        return <Shield className="w-4 h-4 text-blue-600" />;
      default:
        return <User className="w-4 h-4 text-gray-600" />;
    }
  };

  if (!isOpen) return null;

  const handleInviteUser = async () => {
    try {
      await inviteUser(inviteEmail);
      setShowInviteUser(false);
      setInviteEmail('');
      loadInvitations();
      alert('Invitation sent successfully!');
    } catch (error) {
      console.error('Failed to invite user:', error);
      alert(error.message);
    }
  };

  const handleUpdateUserCredits = async () => {
    if (!selectedUser) return;
    
    try {
      await updateUserCredits(selectedUser.id, newCredits);
      setShowEditCredits(false);
      loadUsers();
    } catch (error) {
      console.error('Failed to update credits:', error);
      alert('Failed to update user credits');
    }
  };

  const handleResetUserCredits = async () => {
    if (!selectedUser) return;
    
    try {
      await resetUserCredits(selectedUser.id);
      setShowResetCredits(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to reset credits:', error);
      alert('Failed to reset user credits');
    }
  };

  const handleUpdateUserStatus = async (userId, status) => {
    try {
      await updateUserStatus(userId, status);
      loadUsers();
    } catch (error) {
      console.error('Failed to update user status:', error);
      alert('Failed to update user status');
    }
  };

  const handleUpdateUserAdminStatus = async (userId, role) => {
    try {
      const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
      const isSuperAdmin = role === 'SUPER_ADMIN';
      await updateUserAdminStatus(userId, isAdmin, isSuperAdmin);
      loadUsers();
    } catch (error) {
      console.error('Failed to update user admin status:', error);
      alert(error.message || 'Failed to update user admin status');
    }
  };

  const getUserStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'INVITED':
        return 'bg-yellow-100 text-yellow-800';
      case 'SUSPENDED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold">Team Management</h2>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('teams')}
                className={`px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'teams'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Teams
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'users'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Users
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {activeTab === 'teams' ? (
          <div className="flex flex-1 overflow-hidden">
            {/* Teams List */}
            <div className="w-1/3 border-r p-4 overflow-y-auto">
              <button
                onClick={() => setShowCreateForm(true)}
                className="w-full flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mb-4"
              >
                <Plus className="w-4 h-4" />
                Create Team
              </button>

              <div className="space-y-2">
                {teams.map(team => (
                  <button
                    key={team.id}
                    onClick={() => setSelectedTeam(team)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedTeam?.id === team.id
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{team.name}</h3>
                        <p className="text-sm text-gray-600">
                          {team._count.members} members
                        </p>
                      </div>
                      <Users className="w-5 h-5 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Team Details */}
            <div className="flex-1 p-6 overflow-y-auto">
              {selectedTeam ? (
              <>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{selectedTeam.name}</h3>
                      {selectedTeam.description && (
                        <p className="text-gray-600 mt-1">{selectedTeam.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowPermissions(true)}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <SettingsIcon className="w-4 h-4" />
                        Permissions
                      </button>
                      {selectedTeam.ownerId === selectedTeam.owner.id && (
                        <button
                          onClick={() => {
                            setTeamToDelete(selectedTeam);
                            setDeleteModalOpen(true);
                          }}
                          className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={selectedTeam.owner.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedTeam.owner.name || 'User')}`}
                        alt={selectedTeam.owner.name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <p className="font-medium">{selectedTeam.owner.name}</p>
                        <p className="text-sm text-gray-600">Team Owner</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4 flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Members</h4>
                  <button
                    onClick={() => setShowAddMember(true)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Member
                  </button>
                </div>

                <div className="space-y-2">
                  {selectedTeam.members.map(member => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={member.user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.user.name || 'User')}`}
                          alt={member.user.name}
                          className="w-8 h-8 rounded-full"
                        />
                        <div>
                          <p className="font-medium">{member.user.name}</p>
                          <p className="text-sm text-gray-600">{member.user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          {getRoleIcon(member.role)}
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                            disabled={member.role === 'OWNER'}
                            className="text-sm border rounded px-2 py-1"
                          >
                            <option value="MEMBER">Member</option>
                            <option value="ADMIN">Admin</option>
                            {member.role === 'OWNER' && <option value="OWNER">Owner</option>}
                          </select>
                        </div>
                        {member.role !== 'OWNER' && (
                          <button
                            onClick={() => setMemberToRemove(member)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  {teams.length === 0 ? 'Create your first team' : 'Select a team'}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Users Tab */
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">All Users</h3>
                <button
                  onClick={() => setShowInviteUser(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Invite User
                </button>
              </div>

              {/* Pending Invitations */}
              {invitations.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3">Pending Invitations</h4>
                  <div className="space-y-2">
                    {invitations.map(invitation => (
                      <div key={invitation.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <div>
                          <p className="font-medium">{invitation.email}</p>
                          <p className="text-sm text-gray-600">
                            Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                            {invitation.team && ` • Team: ${invitation.team.name}`}
                          </p>
                        </div>
                        <Clock className="w-5 h-5 text-yellow-600" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Users List */}
              <div className="space-y-3">
                <h4 className="text-lg font-semibold mb-3">Users</h4>
                {users.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <img
                        src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}`}
                        alt={user.name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <p className="font-medium">{user.name || 'Unnamed User'}</p>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${getUserStatusColor(user.status)}`}>
                            {user.status}
                          </span>
                          {user.isAdmin && (
                            <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800">
                              ADMIN
                            </span>
                          )}
                          <span className="text-xs text-gray-500">
                            Teams: {user._count.memberships}
                          </span>
                          {user.inviter && (
                            <span className="text-xs text-gray-500">
                              Invited by: {user.inviter.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-sm">
                          <CreditCard className="w-4 h-4 text-gray-500" />
                          <span>{user.monthlyCredits - user.creditsUsed} / {user.monthlyCredits}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Resets: {new Date(user.creditsResetAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setNewCredits(user.monthlyCredits);
                            setShowEditCredits(true);
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                          title="Edit Credits"
                        >
                          <CreditCard className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowResetCredits(true);
                          }}
                          className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                          title="Reset Credits to 0"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <select
                          value={user.isSuperAdmin ? 'SUPER_ADMIN' : user.isAdmin ? 'ADMIN' : 'USER'}
                          onChange={(e) => handleUpdateUserAdminStatus(user.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                          title="Admin Status"
                        >
                          <option value="USER">User</option>
                          <option value="ADMIN">Admin</option>
                          <option value="SUPER_ADMIN">Super Admin</option>
                        </select>
                        <select
                          value={user.status}
                          onChange={(e) => handleUpdateUserStatus(user.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="ACTIVE">Active</option>
                          <option value="SUSPENDED">Suspended</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Create New Team</h3>
            <input
              type="text"
              placeholder="Team name"
              value={newTeam.name}
              onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-3"
            />
            <textarea
              placeholder="Description (optional)"
              value={newTeam.description}
              onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-4 h-20"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTeam}
                disabled={!newTeam.name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Add Team Member</h3>
            <input
              type="email"
              placeholder="Email address"
              value={newMember.email}
              onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-3"
            />
            <select
              value={newMember.role}
              onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-4"
            >
              <option value="MEMBER">Member</option>
              <option value="ADMIN">Admin</option>
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddMember(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                disabled={!newMember.email}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissions && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Team Permissions</h3>
            
            <div className="mb-6">
              <h4 className="font-medium mb-2">Additional Team Models</h4>
              <p className="text-sm text-gray-600 mb-3">
                Select additional models this team can access. These will be added to the global models available to all users.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Team members will have access to both global models (set in Settings → Models) 
                  plus any additional models selected here.
                </p>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                {Object.entries(models).filter(([provider, modelList]) => Array.isArray(modelList) && modelList.length > 0).map(([provider, modelList]) => (
                  <div key={provider}>
                    <h5 className="text-sm font-medium text-gray-700 mb-1 capitalize">{provider}</h5>
                    {(modelList || []).map(model => (
                      <label key={model.id} className="flex items-center gap-2 py-1">
                        <input
                          type="checkbox"
                          checked={permissions.allowedModels.includes(model.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPermissions({
                                ...permissions,
                                allowedModels: [...permissions.allowedModels, model.id],
                              });
                            } else {
                              setPermissions({
                                ...permissions,
                                allowedModels: permissions.allowedModels.filter(id => id !== model.id),
                              });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{model.name}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  checked={permissions.enableBurstMode}
                  onChange={(e) => setPermissions({
                    ...permissions,
                    enableBurstMode: e.target.checked,
                  })}
                  className="rounded"
                />
                <span className="font-medium flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-600" />
                  Enable Burst Mode
                </span>
              </label>
              <p className="text-sm text-gray-600 mb-4">Allow team members to use burst mode for comparing multiple model responses</p>
            </div>

            <div className="mb-6">
              <label className="block font-medium mb-2">
                Monthly Credits Override (optional)
              </label>
              <input
                type="number"
                value={permissions.monthlyCredits || ''}
                onChange={(e) => setPermissions({
                  ...permissions,
                  monthlyCredits: e.target.value ? parseInt(e.target.value) : null,
                })}
                placeholder="Leave empty to use user's default credits"
                className="w-full px-3 py-2 border rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-1">Overrides individual user credit limits for team members</p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowPermissions(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePermissions}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setTeamToDelete(null);
        }}
        onConfirm={handleDeleteTeam}
        title="Delete Team"
        message={`Are you sure you want to delete "${teamToDelete?.name}"? This will remove all members and cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Remove Member Confirmation */}
      <ConfirmationModal
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleRemoveMember}
        title="Remove Member"
        message={`Are you sure you want to remove ${memberToRemove?.user.name} from the team?`}
        confirmText="Remove"
        cancelText="Cancel"
        type="warning"
      />

      {/* Invite User Modal */}
      {showInviteUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Invite New User</h3>
            <input
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg mb-4"
            />
            <p className="text-sm text-gray-600 mb-4">
              An invitation email will be sent to this address with a link to join.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowInviteUser(false);
                  setInviteEmail('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInviteUser}
                disabled={!inviteEmail || !inviteEmail.includes('@')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Credits Modal */}
      {showEditCredits && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Edit Monthly Credits</h3>
            <p className="text-sm text-gray-600 mb-2">
              User: {selectedUser.name} ({selectedUser.email})
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Current usage: {selectedUser.creditsUsed} / {selectedUser.monthlyCredits}
            </p>
            <label className="block font-medium mb-2">
              New Monthly Credit Limit
            </label>
            <input
              type="number"
              value={newCredits}
              onChange={(e) => setNewCredits(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border rounded-lg mb-4"
              min="0"
              step="1000"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowEditCredits(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUserCredits}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update Credits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset User Credits Confirmation Modal */}
      {showResetCredits && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 text-orange-600">Reset User Credits</h3>
            <p className="text-sm text-gray-600 mb-2">
              User: {selectedUser.name} ({selectedUser.email})
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Current usage: <span className="font-medium">{selectedUser.creditsUsed} / {selectedUser.monthlyCredits}</span>
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-orange-800">
                <strong>Warning:</strong> This will reset the user's used credits to 0, giving them their full monthly allowance again. 
                This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowResetCredits(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetUserCredits}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Reset Credits
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;